import * as THREE from "./three.module.min.js";
import {
  chooseMemorySource,
  createMemoryFeedRequest,
  getArchiveSpriteCount,
  selectStormMemories,
} from "namski-memory-feed";

const VERTEX_SHADER = `
  uniform float uTime;
  uniform float uZoom;
  uniform float uPixelRatio;
  uniform float uPointSize;
  uniform vec3 uPointer;
  uniform float uPointerStrength;
  uniform float uPointerRadius;
  uniform float uPointerPressure;
  uniform float uPointerIrregularity;
  uniform sampler2D uDensityMap;
  attribute float aSeed;
  attribute float aLayer;
  varying float vSeed;
  varying float vLayer;
  varying float vDensity;
  varying float vAlive;
  varying float vPointerInfluence;
  varying float vLocalDepth;

  mat2 rotate2d(float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return mat2(c, -s, s, c);
  }

  void main() {
    vSeed = aSeed;
    vLayer = aLayer;
    vec3 densitySample = texture2D(uDensityMap, uv).rgb;
    float density = dot(densitySample, vec3(0.299, 0.587, 0.114));
    float alive = step(aSeed, pow(density, 1.08));
    vDensity = density;
    vAlive = alive;
    vec3 p = position;

    p.z += (density - 0.45) * 0.17;
    p.z *= 0.45 + density * 0.72;
    vec2 radial = normalize(p.xy + vec2(0.0001));
    p.xy += radial * sin(uTime * 0.72 + aSeed * 18.0) * 0.024 * (1.0 - uZoom * 0.5);

    float loose = (1.0 - uZoom) * (0.13 + aLayer * 0.42);
    float curl = uTime * (0.055 + aSeed * 0.018) + p.z * 0.35;
    p.xy = rotate2d(curl * loose) * p.xy;
    p.x += sin(uTime * 0.48 + aSeed * 31.0) * loose;
    p.y += cos(uTime * 0.39 + aSeed * 27.0) * loose * 0.62;
    p.z += sin(uTime * 0.31 + aSeed * 14.0) * loose * 1.8;

    vec3 pointerDelta = p - uPointer;
    float pointerDistance = length(pointerDelta);
    float particleNoise = fract(sin(aSeed * 127.1 + aLayer * 311.7) * 43758.5453);
    float pointerAngle = atan(pointerDelta.y, pointerDelta.x);
    float boundaryNoise = sin(pointerAngle * 5.0 + aSeed * 8.0 + uTime * 0.34) * 0.11
      + sin(pointerAngle * 9.0 - aSeed * 5.0) * 0.055;
    float localRadius = uPointerRadius * (1.0 + boundaryNoise * uPointerIrregularity);
    float pressureFalloff = 1.0 - smoothstep(localRadius * 0.1, localRadius, pointerDistance);
    pressureFalloff = pow(max(pressureFalloff, 0.0), 1.58);
    float pressureVariation = mix(1.0, 0.58 + particleNoise * 0.86, uPointerIrregularity);
    float pointerInfluence = pressureFalloff * pressureVariation * uPointerStrength * alive;
    vec2 radialDirection = normalize(pointerDelta.xy + vec2(0.0001));
    vec2 tangentDirection = vec2(-radialDirection.y, radialDirection.x);
    float tangentNoise = sin(aSeed * 41.0 + uTime * 1.15);
    p.xy += radialDirection * pointerInfluence * uPointerPressure * (0.18 + aLayer * 0.1);
    p.xy += tangentDirection * pointerInfluence * uPointerPressure * uPointerIrregularity * tangentNoise * 0.085;
    p.z += pointerInfluence * uPointerPressure * (0.36 + aLayer * 0.24 + particleNoise * 0.1);
    vPointerInfluence = pointerInfluence;
    vLocalDepth = p.z;

    vec4 viewPosition = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * viewPosition;
    float perspective = 300.0 / max(1.0, -viewPosition.z);
    gl_PointSize = uPointSize * uPixelRatio * perspective * (0.38 + density * 0.74 + aLayer * 0.34)
      * alive * (1.0 + pointerInfluence * 0.72);
  }
`;

const FRAGMENT_SHADER = `
  uniform float uOpacity;
  uniform vec3 uStormColourA;
  uniform vec3 uStormColourB;
  uniform float uStormLift;
  uniform float uHoverBrightness;
  varying float vSeed;
  varying float vLayer;
  varying float vDensity;
  varying float vAlive;
  varying float vPointerInfluence;
  varying float vLocalDepth;

  void main() {
    vec2 centred = gl_PointCoord - vec2(0.5);
    float distanceToCentre = length(centred);
    float core = 1.0 - smoothstep(0.08, 0.48, distanceToCentre);
    float glow = 1.0 - smoothstep(0.18, 0.5, distanceToCentre);
    float flicker = 0.72 + 0.28 * sin(vSeed * 91.0);
    float tonalDensity = smoothstep(0.015, 0.96, vDensity);
    float alpha = (core * 0.74 + glow * 0.34) * uOpacity * flicker * tonalDensity * vAlive;
    vec3 colour = mix(uStormColourA, uStormColourB, fract(vSeed * 7.0));
    colour = mix(colour, vec3(1.0), clamp(uStormLift + vLayer * 0.24, 0.0, 1.0));
    colour *= mix(0.56, 1.0, smoothstep(-0.72, 0.72, vLocalDepth));
    alpha *= 1.0 + vPointerInfluence * uHoverBrightness;
    gl_FragColor = vec4(colour, alpha);
  }
`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const lerp = (start, end, amount) => start + (end - start) * amount;
const smoothstep = (min, max, value) => {
  const x = clamp((value - min) / (max - min), 0, 1);
  return x * x * (3 - 2 * x);
};

function makeRandom(seed = 9481) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function createDensityGeometry(columns, rows, seedOffset = 0) {
  const count = columns * rows;
  const random = makeRandom(858 + seedOffset);
  const positions = new Float32Array(count * 3);
  const uvs = new Float32Array(count * 2);
  const seeds = new Float32Array(count);
  const layers = new Float32Array(count);

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const index = row * columns + column;
      const u = clamp((column + .5) / columns + (random() - .5) / columns, .001, .999);
      const v = clamp((row + .5) / rows + (random() - .5) / rows, .001, .999);
      const layer = Math.pow(random(), 2.15);
      positions[index * 3] = (u - .5) * 3.72;
      positions[index * 3 + 1] = (.5 - v) * 3.686;
      positions[index * 3 + 2] = (random() - .5) * 1.42;
      uvs[index * 2] = u;
      uvs[index * 2 + 1] = 1 - v;
      seeds[index] = random();
      layers[index] = layer;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 1));
  geometry.setAttribute("aLayer", new THREE.BufferAttribute(layers, 1));
  return geometry;
}

function samplePositions(image, count) {
  const size = 320;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return [];
  context.drawImage(image, 0, 0, size, size);
  const pixels = context.getImageData(0, 0, size, size).data;
  const random = makeRandom(6048);
  const positions = [];
  let attempts = 0;

  while (positions.length < count && attempts < count * 180) {
    attempts += 1;
    const u = random();
    const v = random();
    const offset = (Math.min(size - 1, Math.floor(v * size)) * size + Math.min(size - 1, Math.floor(u * size))) * 4;
    const density = (pixels[offset] * .299 + pixels[offset + 1] * .587 + pixels[offset + 2] * .114) / 255;
    if (density < .025 || random() > Math.pow(density, 1.06)) continue;
    positions.push(new THREE.Vector3((u - .5) * 3.72, (.5 - v) * 3.686, (random() - .5) * 1.24 + (density - .45) * .17));
  }
  return positions;
}

async function loadManagedMemories(feedUrl) {
  if (!feedUrl) return { memories: [], loaded: false };
  const memories = [];
  const seen = new Set();
  const refreshToken = `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
  let page = 1;
  let pages = 1;

  try {
    do {
      const request = createMemoryFeedRequest(
        feedUrl,
        window.location.href,
        page,
        refreshToken,
      );
      const response = await fetch(request.url, request.options);
      if (!response.ok) throw new Error(`Memory feed returned ${response.status}`);
      const payload = await response.json();
      const items = Array.isArray(payload.items) ? payload.items : [];
      items.forEach((item) => {
        if (!item?.thumbnail || !item?.display) return;
        const key = String(item.key || item.display).trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        memories.push(item);
      });
      pages = Math.max(1, Number(payload.pages) || 1);
      page += 1;
    } while (page <= pages);
  } catch (error) {
    console.warn("NAMSKI memory feed could not be loaded; showing an empty archive.", error);
    return { memories: [], loaded: false };
  }

  return { memories, loaded: true };
}

async function initialize(root) {
  const configNode = root.querySelector("[data-namski-memory-config]");
  const host = root.querySelector("[data-namski-memory-webgl]");
  const viewer = root.querySelector("[data-namski-memory-viewer]");
  const viewerStage = root.querySelector("[data-namski-memory-viewer-stage]");
  const viewerImage = root.querySelector("[data-namski-memory-viewer-image]");
  const viewerAdjacent = root.querySelector("[data-namski-memory-viewer-adjacent]");
  const viewerPrevious = root.querySelector("[data-namski-memory-viewer-previous]");
  const viewerNext = root.querySelector("[data-namski-memory-viewer-next]");
  if (!configNode || !host || !viewer || !viewerStage || !viewerImage || !viewerAdjacent || !viewerPrevious || !viewerNext || root.dataset.initialized === "true") return;
  root.dataset.initialized = "true";
  const config = JSON.parse(configNode.textContent);
  config.density = new URL(config.density, window.location.href).href;
  const managedFeed = await loadManagedMemories(config.feed);
  const memories = chooseMemorySource(managedFeed);
  const stormMemories = selectStormMemories(memories);
  config.images = stormMemories.map((memory) => memory.thumbnail);
  config.stormArchiveIndexes = stormMemories.map((memory) => memory.archiveIndex);
  config.displayImages = memories.map((memory) => memory.display);
  config.displayAlts = memories.map((memory) => memory.alt || config.defaultAlt || "Archive image");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const colours = config.colours;
  const hover = config.hover;
  const atmosphere = config.atmosphere;

  const closeViewer = () => viewer.close();
  let viewerImageIndex = 0;
  let viewerSwipe = null;
  let viewerAnimation = null;
  let viewerIgnoreClick = false;
  const viewerOrder = Array.from({ length: config.displayImages.length }, (_, index) => index);
  for (let index = viewerOrder.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [viewerOrder[index], viewerOrder[swapIndex]] = [viewerOrder[swapIndex], viewerOrder[index]];
  }
  const normalizeViewerIndex = (index) => (index + config.displayImages.length) % config.displayImages.length;
  const getViewerSource = (index) => config.displayImages[viewerOrder[normalizeViewerIndex(index)]];
  const getViewerAlt = (index) => config.displayAlts[viewerOrder[normalizeViewerIndex(index)]];
  const viewerTransform = (offset) => `translate(-50%, -50%) translate3d(${offset}px, 0, 0)`;
  const syncIntrinsicDimensions = (image) => {
    if (!image.naturalWidth || !image.naturalHeight) return;
    image.width = image.naturalWidth;
    image.height = image.naturalHeight;
  };
  const preloadViewerNeighbours = () => {
    if (config.displayImages.length === 0) return;
    [-1, 1].forEach((direction) => {
      const image = new Image();
      image.src = getViewerSource(viewerImageIndex + direction);
    });
  };
  const showViewerImage = (index) => {
    if (config.displayImages.length === 0) return;
    viewerImageIndex = normalizeViewerIndex(index);
    viewerImage.src = getViewerSource(viewerImageIndex);
    viewerImage.alt = getViewerAlt(viewerImageIndex);
    preloadViewerNeighbours();
  };
  const clearAdjacentImage = () => {
    viewerAdjacent.style.visibility = "hidden";
    viewerAdjacent.style.transform = viewerTransform(0);
    viewerAdjacent.removeAttribute("src");
    delete viewerAdjacent.dataset.imageIndex;
  };
  const prepareAdjacentImage = (direction, startX = 0) => {
    const targetIndex = normalizeViewerIndex(viewerImageIndex + direction);
    const stageWidth = Math.max(1, viewerStage.clientWidth);
    const targetSource = getViewerSource(targetIndex);
    if (viewerAdjacent.src !== targetSource) viewerAdjacent.src = targetSource;
    viewerAdjacent.dataset.imageIndex = String(targetIndex);
    viewerAdjacent.style.visibility = "visible";
    viewerAdjacent.style.transform = viewerTransform(direction * stageWidth + startX);
    return { targetIndex, stageWidth };
  };
  const positionViewerTrack = (direction, deltaX) => {
    const { stageWidth } = prepareAdjacentImage(direction, deltaX);
    viewerImage.style.transform = viewerTransform(deltaX);
    viewerAdjacent.style.transform = viewerTransform(direction * stageWidth + deltaX);
  };
  const animateViewerImage = async (direction, startX = 0, shouldNavigate = true) => {
    if (viewerAnimation || !viewer.open) return;
    viewerImage.classList.remove("is-swiping");
    const { targetIndex, stageWidth } = prepareAdjacentImage(direction, startX);
    if (reduceMotion) {
      if (shouldNavigate) showViewerImage(targetIndex);
      viewerImage.style.transform = viewerTransform(0);
      clearAdjacentImage();
      return;
    }
    const currentEnd = shouldNavigate ? -direction * stageWidth : 0;
    const adjacentStart = direction * stageWidth + startX;
    const adjacentEnd = shouldNavigate ? 0 : direction * stageWidth;
    const duration = shouldNavigate ? 360 : 300;
    const currentAnimation = viewerImage.animate([
      { transform: viewerTransform(startX) },
      { transform: viewerTransform(currentEnd) },
    ], {
      duration,
      easing: "cubic-bezier(.22, 1, .36, 1)",
      fill: "forwards",
    });
    const adjacentAnimation = viewerAdjacent.animate([
      { transform: viewerTransform(adjacentStart) },
      { transform: viewerTransform(adjacentEnd) },
    ], {
      duration,
      easing: "cubic-bezier(.22, 1, .36, 1)",
      fill: "forwards",
    });
    viewerAnimation = {
      cancel() {
        currentAnimation.cancel();
        adjacentAnimation.cancel();
      },
    };
    try {
      await Promise.all([currentAnimation.finished, adjacentAnimation.finished]);
      if (!viewer.open) return;
      if (shouldNavigate) {
        viewerImage.style.visibility = "hidden";
        viewerImageIndex = targetIndex;
        viewerImage.src = viewerAdjacent.src;
        viewerImage.alt = getViewerAlt(viewerImageIndex);
        viewerImage.width = viewerAdjacent.naturalWidth || viewerAdjacent.width;
        viewerImage.height = viewerAdjacent.naturalHeight || viewerAdjacent.height;
        try {
          await viewerImage.decode();
        } catch {
          // The adjacent image is already visible while the browser finalizes the promoted image.
        }
        viewerImage.style.transform = viewerTransform(0);
        viewerImage.style.visibility = "visible";
        preloadViewerNeighbours();
      } else {
        viewerImage.style.transform = viewerTransform(0);
      }
      clearAdjacentImage();
      currentAnimation.cancel();
      adjacentAnimation.cancel();
    } catch {
      viewerAnimation?.cancel();
      viewerImage.style.transform = viewerTransform(0);
      clearAdjacentImage();
    } finally {
      viewerAnimation = null;
    }
  };
  const finishViewerSwipe = (event, allowNavigation) => {
    if (!viewerSwipe || viewerSwipe.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - viewerSwipe.startX;
    if (Math.abs(deltaX) > 8) {
      viewerIgnoreClick = true;
      window.setTimeout(() => { viewerIgnoreClick = false; }, 0);
    }
    const threshold = Math.min(96, Math.max(44, viewerImage.clientWidth * .14));
    if (viewerStage.hasPointerCapture?.(event.pointerId)) {
      viewerStage.releasePointerCapture(event.pointerId);
    }
    viewerImage.classList.remove("is-swiping");
    if (allowNavigation && Math.abs(deltaX) >= threshold) {
      const direction = deltaX < 0 ? 1 : -1;
      viewerSwipe = null;
      animateViewerImage(direction, deltaX, true);
    } else {
      const direction = viewerSwipe.direction || (deltaX < 0 ? 1 : -1);
      viewerSwipe = null;
      animateViewerImage(direction, deltaX, false);
    }
  };
  viewerPrevious.addEventListener("click", () => animateViewerImage(-1));
  viewerNext.addEventListener("click", () => animateViewerImage(1));
  viewerImage.addEventListener("load", () => syncIntrinsicDimensions(viewerImage));
  viewerAdjacent.addEventListener("load", () => syncIntrinsicDimensions(viewerAdjacent));
  viewerStage.addEventListener("pointerdown", (event) => {
    if (event.target !== viewerImage || viewerAnimation || (event.pointerType === "mouse" && event.button !== 0)) return;
    viewerSwipe = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      direction: 0,
    };
    viewerStage.setPointerCapture?.(event.pointerId);
    viewerImage.classList.add("is-swiping");
  });
  viewerStage.addEventListener("pointermove", (event) => {
    if (!viewerSwipe || viewerSwipe.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - viewerSwipe.startX;
    const deltaY = event.clientY - viewerSwipe.startY;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    event.preventDefault();
    const resistance = 1 - Math.min(Math.abs(deltaX) / Math.max(1, viewerImage.clientWidth), 1) * .12;
    const renderedDelta = deltaX * .96 * resistance;
    const direction = renderedDelta < 0 ? 1 : -1;
    viewerSwipe.direction = direction;
    positionViewerTrack(direction, renderedDelta);
  });
  viewerStage.addEventListener("pointerup", (event) => finishViewerSwipe(event, true));
  viewerStage.addEventListener("pointercancel", (event) => finishViewerSwipe(event, false));
  viewer.addEventListener("click", (event) => {
    if (viewerIgnoreClick) return;
    if (event.target === viewer || event.target === viewerStage) closeViewer();
  });
  viewer.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      animateViewerImage(-1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      animateViewerImage(1);
    }
  });
  viewer.addEventListener("close", () => {
    viewerSwipe = null;
    viewerAnimation?.cancel();
    viewerAnimation = null;
    viewerImage.classList.remove("is-swiping");
    viewerImage.style.visibility = "visible";
    viewerImage.style.transform = viewerTransform(0);
    viewerImage.removeAttribute("src");
    clearAdjacentImage();
  });

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(colours.background, atmosphere.fogDensity);
  const camera = new THREE.PerspectiveCamera(42, 1, .1, 40);
  camera.position.set(0, 0, 7.35);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setClearColor(colours.background, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.className = "namskiMemory__canvas";
  renderer.domElement.setAttribute("aria-label", "Interactive particle archive shaped as the NAMSKI mark");
  host.appendChild(renderer.domElement);

  const markGroup = new THREE.Group();
  scene.add(markGroup);
  const textureLoader = new THREE.TextureLoader();
  let applyMask = () => {};
  const densityTexture = textureLoader.load(config.density, (texture) => applyMask(texture.image));
  densityTexture.colorSpace = THREE.NoColorSpace;
  densityTexture.minFilter = THREE.LinearMipmapLinearFilter;
  densityTexture.magFilter = THREE.LinearFilter;

  const geometry = createDensityGeometry(216, 214);
  const uniforms = {
    uTime: { value: 0 },
    uZoom: { value: 0 },
    uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
    uPointSize: { value: .052 },
    uOpacity: { value: atmosphere.particleOpacity },
    uStormColourA: { value: new THREE.Color(colours.stormA) },
    uStormColourB: { value: new THREE.Color(colours.stormB) },
    uStormLift: { value: atmosphere.stormLift },
    uHoverBrightness: { value: hover.brightness },
    uPointer: { value: new THREE.Vector3(100, 100, 0) },
    uPointerStrength: { value: 0 },
    uPointerRadius: { value: hover.radius },
    uPointerPressure: { value: hover.pressure },
    uPointerIrregularity: { value: hover.irregularity },
    uDensityMap: { value: densityTexture },
  };
  const pointMaterial = new THREE.ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms,
  });
  markGroup.add(new THREE.Points(geometry, pointMaterial));

  const textures = config.images.map((source) => {
    const texture = textureLoader.load(source);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  });
  const archiveGroup = new THREE.Group();
  markGroup.add(archiveGroup);
  const archiveRandom = makeRandom(1518);
  const sprites = [];

  const archiveSpriteCount = getArchiveSpriteCount(config.images);

  for (let index = 0; index < archiveSpriteCount; index += 1) {
    const material = new THREE.SpriteMaterial({
      map: textures[index],
      color: colours.archiveTint,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: true,
      fog: true,
    });
    const sprite = new THREE.Sprite(material);
    const phase = archiveRandom() * Math.PI * 2;
    const base = new THREE.Vector3(Math.cos(phase) * 1.5, Math.sin(phase) * 1.15, (archiveRandom() - .5) * .9);
    sprite.position.copy(base);
    sprite.scale.setScalar(.018);
    sprite.userData = {
      base,
      normal: base.clone().normalize(),
      scaleSeed: .74 + archiveRandom() * .6,
      phase,
      drift: archiveRandom(),
      imageIndex: config.stormArchiveIndexes[index],
    };
    archiveGroup.add(sprite);
    sprites.push(sprite);
  }

  applyMask = (image) => {
    const sampled = samplePositions(image, sprites.length);
    sprites.forEach((sprite, index) => {
      if (!sampled[index]) return;
      sprite.position.copy(sampled[index]);
      sprite.userData.base = sampled[index];
      sprite.userData.normal = sampled[index].clone().normalize();
    });
  };
  if (densityTexture.image) applyMask(densityTexture.image);

  const haloGeometry = createDensityGeometry(116, 115, 401);
  const haloMaterial = pointMaterial.clone();
  haloMaterial.uniforms = THREE.UniformsUtils.clone(uniforms);
  haloMaterial.uniforms.uPointSize.value = .032;
  haloMaterial.uniforms.uOpacity.value = atmosphere.haloOpacity;
  haloMaterial.uniforms.uDensityMap.value = densityTexture;
  const halo = new THREE.Points(haloGeometry, haloMaterial);
  halo.scale.setScalar(1.2);
  markGroup.add(halo);

  let zoomTarget = 0;
  let zoom = 0;
  let zoomVelocity = 0;
  let targetRotationX = -.035;
  let targetRotationY = 0;
  let rotationX = targetRotationX;
  let rotationY = targetRotationY;
  let pointerDown = false;
  let previousPointerX = 0;
  let previousPointerY = 0;
  let dragVelocityX = 0;
  let dragVelocityY = 0;
  const activePointers = new Map();
  let pinchStartDistance = 0;
  let pinchStartZoom = 0;
  let tapCandidate = null;
  let gestureHadMultiplePointers = false;
  const pointerTarget = new THREE.Vector3(100, 100, 0);
  const pointerCurrent = pointerTarget.clone();
  let pointerStrengthTarget = 0;
  let pointerStrength = 0;
  const raycaster = new THREE.Raycaster();
  const pointerNdc = new THREE.Vector2();
  const interactionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const pointerWorld = new THREE.Vector3();
  let previousFrameTime = performance.now();
  let elapsedTime = 0;

  const resize = () => {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.fov = width < 720 ? 50 : 42;
    camera.updateProjectionMatrix();
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  resize();

  const updatePointer = (event) => {
    const bounds = renderer.domElement.getBoundingClientRect();
    pointerNdc.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointerNdc.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    raycaster.setFromCamera(pointerNdc, camera);
    if (!raycaster.ray.intersectPlane(interactionPlane, pointerWorld)) {
      pointerStrengthTarget = 0;
      return;
    }
    markGroup.updateMatrixWorld(true);
    pointerTarget.copy(pointerWorld);
    markGroup.worldToLocal(pointerTarget);
    pointerStrengthTarget = 1;
  };

  const getPointerDistance = () => {
    const pointers = Array.from(activePointers.values());
    if (pointers.length < 2) return 0;
    return Math.hypot(pointers[1].x - pointers[0].x, pointers[1].y - pointers[0].y);
  };

  const pickArchiveImage = (event) => {
    if (zoomTarget < .14 || sprites.length === 0 || config.displayImages.length === 0) return;
    const bounds = renderer.domElement.getBoundingClientRect();
    pointerNdc.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    pointerNdc.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    raycaster.setFromCamera(pointerNdc, camera);
    scene.updateMatrixWorld(true);
    const hit = raycaster.intersectObjects(sprites, false)
      .find((intersection) => intersection.object.material.opacity > .12);
    const imageIndex = hit?.object.userData.imageIndex;
    if (!Number.isInteger(imageIndex)) return;
    showViewerImage(viewerOrder.indexOf(imageIndex));
    viewer.showModal();
  };

  renderer.domElement.addEventListener("wheel", (event) => {
    event.preventDefault();
    const scale = event.deltaMode === 1 ? 16 : 1;
    zoomTarget = clamp(zoomTarget + event.deltaY * scale * .00072, 0, 1);
  }, { passive: false });
  renderer.domElement.addEventListener("pointerdown", (event) => {
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    pointerDown = true;
    renderer.domElement.setPointerCapture?.(event.pointerId);
    renderer.domElement.classList.add("is-dragging");
    if (activePointers.size === 2) {
      tapCandidate = null;
      gestureHadMultiplePointers = true;
      pinchStartDistance = getPointerDistance();
      pinchStartZoom = zoomTarget;
      dragVelocityX = 0;
      dragVelocityY = 0;
      return;
    }
    tapCandidate = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      startedAt: performance.now(),
    };
    previousPointerX = event.clientX;
    previousPointerY = event.clientY;
  });
  renderer.domElement.addEventListener("pointermove", (event) => {
    updatePointer(event);
    if (!activePointers.has(event.pointerId)) return;
    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (activePointers.size === 2) {
      const distance = getPointerDistance();
      if (pinchStartDistance > 0 && distance > 0) {
        zoomTarget = clamp(pinchStartZoom + Math.log(distance / pinchStartDistance) * 1.15, 0, 1);
      }
      return;
    }
    if (tapCandidate?.pointerId === event.pointerId) {
      const moved = Math.hypot(event.clientX - tapCandidate.x, event.clientY - tapCandidate.y);
      if (moved > 9) tapCandidate = null;
    }
    dragVelocityY = (event.clientX - previousPointerX) * .0048;
    dragVelocityX = (event.clientY - previousPointerY) * .0038;
    targetRotationY += dragVelocityY;
    targetRotationX = clamp(targetRotationX + dragVelocityX, -.72, .72);
    previousPointerX = event.clientX;
    previousPointerY = event.clientY;
  });
  const releasePointer = (event, allowPick) => {
    const shouldPick = allowPick
      && activePointers.size === 1
      && !gestureHadMultiplePointers
      && tapCandidate?.pointerId === event.pointerId
      && performance.now() - tapCandidate.startedAt < 650;
    activePointers.delete(event.pointerId);
    if (renderer.domElement.hasPointerCapture?.(event.pointerId)) {
      renderer.domElement.releasePointerCapture(event.pointerId);
    }
    if (activePointers.size === 1) {
      const remainingPointer = activePointers.values().next().value;
      previousPointerX = remainingPointer.x;
      previousPointerY = remainingPointer.y;
      pinchStartDistance = 0;
      pinchStartZoom = zoomTarget;
      pointerDown = true;
      tapCandidate = null;
      return;
    }
    pointerDown = false;
    pinchStartDistance = 0;
    tapCandidate = null;
    gestureHadMultiplePointers = false;
    renderer.domElement.classList.remove("is-dragging");
    if (shouldPick) pickArchiveImage(event);
  };
  renderer.domElement.addEventListener("pointerup", (event) => releasePointer(event, true));
  renderer.domElement.addEventListener("pointercancel", (event) => releasePointer(event, false));
  renderer.domElement.addEventListener("pointerleave", () => {
    if (!pointerDown) pointerStrengthTarget = 0;
  });

  const render = () => {
    const now = performance.now();
    const delta = Math.min((now - previousFrameTime) / 1000, .04);
    previousFrameTime = now;
    elapsedTime += delta;

    if (reduceMotion) {
      zoom = zoomTarget;
    } else {
      zoomVelocity += (zoomTarget - zoom) * 92 * delta;
      zoomVelocity *= Math.pow(.0008, delta);
      zoom = clamp(zoom + zoomVelocity * delta, -.015, 1.015);
    }
    const easedZoom = smoothstep(0, 1, clamp(zoom, 0, 1));
    const pointerFollow = 1 - Math.exp(-20 * delta);
    const pointerRelease = 1 - Math.exp(-12 * delta);
    pointerCurrent.lerp(pointerTarget, pointerFollow);
    pointerStrength += (pointerStrengthTarget - pointerStrength) * pointerRelease;

    if (!pointerDown && !reduceMotion) {
      targetRotationY += .00088 + dragVelocityY;
      targetRotationX += dragVelocityX;
      dragVelocityX *= .91;
      dragVelocityY *= .91;
    }
    rotationX += (targetRotationX - rotationX) * .075;
    rotationY += (targetRotationY - rotationY) * .075;
    markGroup.rotation.x = rotationX + Math.sin(elapsedTime * .31) * (.018 + (1 - easedZoom) * .022);
    markGroup.rotation.y = rotationY + Math.sin(elapsedTime * .21) * .15;
    markGroup.rotation.z = Math.sin(elapsedTime * .19) * .045;
    camera.position.z = lerp(7.35, 3.28, easedZoom);
    camera.position.x = Math.sin(elapsedTime * .16) * .035 * (1 - easedZoom);
    camera.lookAt(0, 0, 0);

    pointMaterial.uniforms.uTime.value = elapsedTime;
    pointMaterial.uniforms.uZoom.value = easedZoom;
    pointMaterial.uniforms.uPointer.value.copy(pointerCurrent);
    pointMaterial.uniforms.uPointerStrength.value = pointerStrength;
    pointMaterial.uniforms.uOpacity.value = lerp(atmosphere.particleOpacity, atmosphere.particleOpacity * .27, easedZoom);
    haloMaterial.uniforms.uTime.value = elapsedTime;
    haloMaterial.uniforms.uZoom.value = easedZoom;
    haloMaterial.uniforms.uPointer.value.copy(pointerCurrent);
    haloMaterial.uniforms.uPointerStrength.value = pointerStrength * .74;
    haloMaterial.uniforms.uOpacity.value = lerp(atmosphere.haloOpacity, .08, easedZoom);

    const materialOpacity = smoothstep(.16, .76, easedZoom) * .94;
    sprites.forEach((sprite, index) => {
      const { base, normal, scaleSeed, phase, drift } = sprite.userData;
      const dx = base.x - pointerCurrent.x;
      const dy = base.y - pointerCurrent.y;
      const dz = base.z - pointerCurrent.z;
      const distance = Math.hypot(dx, dy, dz);
      const irregularRadius = hover.radius * (.9 + Math.sin(phase * 2.7 + elapsedTime * .24) * .1 * hover.irregularity);
      const reveal = (1 - smoothstep(irregularRadius * .12, irregularRadius, distance))
        * pointerStrength * (1 - easedZoom) * (.72 + drift * .28);
      sprite.position.copy(base).addScaledVector(normal, easedZoom * (.12 + drift * .22));
      if (reveal > .001) {
        const length = Math.max(.001, distance);
        const push = reveal * hover.pressure;
        sprite.position.x += (dx / length) * push * .13;
        sprite.position.y += (dy / length) * push * .13;
        sprite.position.z += ((dz / length) * .08 + .22 + drift * .16) * push;
      }
      const unrest = (1 - easedZoom) * (.04 + drift * .12);
      sprite.position.x += Math.sin(elapsedTime * (.22 + drift * .34) + phase) * unrest;
      sprite.position.y += Math.cos(elapsedTime * (.19 + drift * .27) + phase) * unrest;
      const scale = lerp(.014, .168 * scaleSeed, smoothstep(.12, 1, easedZoom));
      sprite.scale.set(scale * 1.32, scale, 1);
      sprite.material.opacity = materialOpacity;
      sprite.material.alphaTest = materialOpacity > .25 ? .02 : 0;
      sprite.renderOrder = index % 7;
    });

    renderer.render(scene, camera);
    requestAnimationFrame(render);
  };
  render();
}

document.querySelectorAll("[data-namski-memory]").forEach(initialize);
